import { useEffect } from 'react';
import { confirm } from './telegram';
import { getLang, translate } from './i18n';

/**
 * A tap on the dim area behind a sheet closes it. That is right for a
 * sheet you only looked at, and wrong the moment anything has been typed
 * into one: on a phone the backdrop is most of the screen, and with the
 * keyboard up it is also where the fingers are. «Получено 80 €» being
 * one careless tap from gone is what made a payment vanish silently.
 *
 * So: a sheet nobody has typed in still closes on a tap outside. A sheet
 * with something typed in it asks first.
 *
 * One listener for every sheet rather than a prop on each of them —
 * there are nineteen of these backdrops, and the next one written should
 * get this for nothing.
 */

/** Typing in a sheet marks the panel; the mark dies with the panel. */
const EDITED = 'edited';

function panelOf(node: EventTarget | null): HTMLElement | null {
  const el = node as HTMLElement | null;
  return el && typeof el.closest === 'function' ? el.closest('.sheet__panel') : null;
}

export function useSheetGuard() {
  useEffect(() => {
    const typed = (e: Event) => {
      const panel = panelOf(e.target);
      if (panel) panel.dataset[EDITED] = '1';
    };

    let asking = false;
    const click = (e: MouseEvent) => {
      const back = e.target as HTMLElement;
      // The backdrop itself: a tap inside the panel has the panel as its
      // target, and the panel stops it from reaching here anyway.
      if (!back?.classList?.contains('sheet')) return;
      const panel = back.querySelector<HTMLElement>('.sheet__panel');
      if (!panel || panel.dataset[EDITED] !== '1') return;

      // Nothing closes until the coach says so.
      e.preventDefault();
      e.stopPropagation();
      if (asking) return;
      asking = true;
      void confirm(translate(getLang(), 'sheet.closeUnsaved')).then((ok) => {
        asking = false;
        if (!ok) return;
        // Asked and answered: the mark goes, and the same tap is made
        // again for the sheet's own handler to act on.
        delete panel.dataset[EDITED];
        back.click();
      });
    };

    document.addEventListener('input', typed, true);
    document.addEventListener('click', click, true);
    return () => {
      document.removeEventListener('input', typed, true);
      document.removeEventListener('click', click, true);
    };
  }, []);
}
